import styled from "@emotion/styled";
import React, { ButtonHTMLAttributes, ReactNode, useMemo } from "react";
import { compassColors } from "@utils/styling";
import { fontP2Medium, fontP2Regular } from "src/design-system/styles/fonts";
import { flexMiddle } from "src/design-system/styles/layout";
import { focusOutline } from "src/design-system/styles/utils";

export const GenericButton = styled.button`
  ${flexMiddle};
  ${fontP2Medium}
  border: none;
  border-radius: 4px;
  box-sizing: border-box;
  padding: 0 16px;
  min-height: 60px;
  width: 100%;
  cursor: pointer;
  text-align: center;
`;

const PrimaryCtaButton = styled(GenericButton)`
  background-color: ${compassColors.tarocco};
  color: ${compassColors.white};

  &:hover {
    background-color: ${compassColors.cinnamon};
  }

  &:disabled {
    background-color: ${compassColors.grey2};
  }

  &:focus {
    border: solid 2px ${compassColors.cinnamon};
    outline: 0;
    ${focusOutline}
  }
`;

const PremiumPrimaryCtaButton = styled(PrimaryCtaButton)`
  background-color: ${compassColors.gold};

  &:hover {
    background-color: ${compassColors.premiumBrown};
  }

  &:focus {
    border: solid 2px ${compassColors.premiumBrown};
  }
`;

const SecondaryCta = styled(GenericButton)`
  background-color: ${compassColors.white};
  color: ${compassColors.grey4};

  &:hover:enabled {
    background-color: ${compassColors.offWhite};
    color: ${compassColors.tarocco};
  }

  &:disabled {
    color: ${compassColors.grey2};
  }

  &:focus {
    border: solid 2px ${compassColors.grey2};
    outline: 0;
    ${focusOutline}
  }
`;

const SingleSelectButton = styled(GenericButton)<{ isSelected: boolean }>`
  ${fontP2Regular}
  background-color: ${compassColors.offWhite};
  // Add a border the same color as the background so that the visible 2px focus border
  // doesn't change the width of the Button, which was happening with Score questions.
  border: solid 2px ${compassColors.offWhite};
  color: ${compassColors.black};
  border-radius: 8px;

  &:disabled {
    opacity: 0.75;
  }

  ${(props) =>
    props.isSelected
      ? `
        background-color: ${compassColors.lagoon};
        border: solid 2px ${compassColors.lagoon};
        color: ${compassColors.offWhite};

        &:focus {
          border: solid 2px ${compassColors.blueberry};
          outline: 0;
        }
        `
      : `
        &:hover:enabled {
          background-color: ${compassColors.sand1};
          border: solid 2px ${compassColors.sand1};
        }

        &:focus {
          border: solid 2px ${compassColors.gold};
          outline: 0;
        }
        `}

  &:focus {
    ${focusOutline}
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const Icon = styled.img<{ height?: string; margin?: Margin }>`
  ${(props) => `height: ${props.height || "18px"};`}
  ${(props) =>
    `margin: ${props.margin?.top || "auto"} ${props.margin?.right || "0"} ${
      props.margin?.bottom || "auto"
    } ${props.margin?.left || "0"};`}
`;

type Props = {
  designStyle?:
    | "primaryCta"
    | "secondaryCta"
    | "singleSelect"
    | "premiumPrimaryCta";
  isSelected?: boolean;
  icon?: IconInfo;
} & ButtonHTMLAttributes<HTMLButtonElement>;

type IconInfo = {
  src: string;
  height?: string;
  margin?: Margin;
  position?: "left" | "right";
};

type Margin = {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
};

function Button({
  designStyle = "primaryCta",
  isSelected,
  icon,
  children: childrenProp,
  ...buttonProps
}: Props) {
  const children = useIcon(icon, childrenProp);

  if (designStyle === "singleSelect") {
    return (
      <SingleSelectButton
        type="button"
        isSelected={isSelected}
        {...buttonProps}
      >
        {children}
      </SingleSelectButton>
    );
  }

  if (designStyle === "secondaryCta") {
    return (
      <SecondaryCta type="button" {...buttonProps}>
        {children}
      </SecondaryCta>
    );
  }

  if (designStyle === "premiumPrimaryCta") {
    return (
      <PremiumPrimaryCtaButton type="button" {...buttonProps}>
        {children}
      </PremiumPrimaryCtaButton>
    );
  }

  return (
    <PrimaryCtaButton type="button" {...buttonProps}>
      {children}
    </PrimaryCtaButton>
  );
}

function useIcon(icon?: IconInfo, children?: ReactNode) {
  return useMemo(() => {
    // Wrap children around an icon container if needed
    if (icon) {
      const { src, height, margin, position = "left" } = icon;
      const iconElement = (
        <Icon
          src={src}
          height={height}
          margin={margin}
          alt=""
          role="presentation"
        />
      );
      return position === "left" ? (
        <IconContainer>
          {iconElement}
          {children}
        </IconContainer>
      ) : (
        <IconContainer>
          {children}
          {iconElement}
        </IconContainer>
      );
    }

    return children;
  }, [children, icon]);
}

export default Button;
